import React, { useRef } from "react";
import dayjs from "dayjs";
import ReactECharts from "echarts-for-react";
import * as echarts from "echarts";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import {
  useGetLSPSStateQuery,
  useGetMicroServiceMonitoringGraphDataQuery,
  useGetTargetDataNowQuery,
  useGetMicroServiceMonitoringForcastQuery,
} from "./operatorDashboardAPISlice";
import GraphDownloadButton from "../../Global/other/GraphDownloadButton";
import { formatSAST } from "../../Global/other/formatSAST";

const labelColor = "rgba(255,255,255,0.45)";
const gridLineColor = "rgba(255,255,255,0.05)";

function makeGradient(color) {
  return new echarts.graphic.LinearGradient(0, 0, 0, 1, [
    { offset: 0, color: color + "55" },
    { offset: 1, color: color + "05" },
  ]);
}

function toTs(items, valueFn) {
  return items.map((item) => [
    new Date(item.interval).getTime(),
    valueFn(item),
  ]);
}

function lineSeries(
  name,
  color,
  data,
  dashed = false,
  withArea = false,
  yAxisIndex = 0,
) {
  return {
    name,
    type: "line",
    smooth: true,
    symbol: "none",
    yAxisIndex,
    lineStyle: {
      color,
      width: 2,
      type: dashed ? "dashed" : "solid",
    },
    areaStyle: withArea ? { color: makeGradient(color) } : undefined,
    itemStyle: { color },
    data,
  };
}

function toCategory(items, valueFn) {
  return items.map((item) => valueFn(item));
}

function barSeries(name, color, data, yAxisIndex = 0) {
  return {
    name,
    type: "bar",
    barMaxWidth: 14,
    yAxisIndex,
    itemStyle: {
      color,
      opacity: 0.65,
      borderRadius: [2, 2, 0, 0],
    },
    emphasis: {
      itemStyle: { opacity: 0.9 },
    },
    data,
  };
}

/** An axis label: the time, with the date when the chart spans more than one day, so a
 *  7-day range doesn't show the same hours seven times over (NEU-1130). */
export function axisTimeLabel(value, spansDays) {
  const d = new Date(value);
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
  return spansDays ? `${d.toLocaleDateString([], { day: "numeric", month: "short" })} ${time}` : time;
}

/** Whether the data covers more than a day, so its axis labels need the date. */
export function spansDays(data) {
  const first = data?.[0]?.interval;
  const last = data?.[data.length - 1]?.interval;
  return !!(first && last) && new Date(last) - new Date(first) > 24 * 60 * 60 * 1000;
}

function buildOption(series, data) {
  const multiDay = spansDays(data);
  return {
    backgroundColor: "transparent",
    animation: false,
    grid: { top: 16, right: 60, bottom: 72, left: 56 },
    dataZoom: [
      { type: "inside", xAxisIndex: 0, filterMode: "filter" },
      {
        type: "slider",
        xAxisIndex: 0,
        height: 18,
        bottom: 32,
        backgroundColor: "rgba(255,255,255,0.03)",
        borderColor: "rgba(255,255,255,0.07)",
        fillerColor: "rgba(253,185,64,0.2)",
        handleStyle: { color: "#FDB940", borderColor: "#FDB940" },
        moveHandleStyle: { color: "#FDB940" },
        emphasis: { handleStyle: { color: "#FDB940" } },
        textStyle: { color: "transparent" },
        brushSelect: false,
        showDetail: false,
      },
    ],
    toolbox: {
      right: 8,
      top: 4,
      feature: {
        dataZoom: {
          yAxisIndex: "none",
          title: { zoom: "Zoom", back: "Reset" },
          iconStyle: { borderColor: labelColor },
          emphasis: { iconStyle: { borderColor: "#2E93FA" } },
        },
        restore: {
          title: "Reset",
          iconStyle: { borderColor: labelColor },
          emphasis: { iconStyle: { borderColor: "#2E93FA" } },
        },
      },
    },
    tooltip: {
      trigger: "axis",
      backgroundColor: "#1C232C",
      borderColor: "rgba(255,255,255,0.12)",
      textStyle: { color: "#e0e6f0", fontSize: 12 },
      formatter: (params) =>
        params
          .map((p) => {
            const val = Array.isArray(p.value) ? p.value[1] : p.value;
            if (val == null) return null;
            const isTjommie = p.seriesName === "Tjommie Load";
            const unit = isTjommie ? "kW" : "MW";
            const decimals = isTjommie ? 1 : 3;
            return (
              `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${p.color};margin-right:6px"></span>` +
              `${p.seriesName}: <b>${Number(val).toFixed(decimals)} ${unit}</b>`
            );
          })
          .filter(Boolean)
          .join("<br/>"),
    },
    legend: {
      bottom: 0,
      textStyle: { color: labelColor, fontSize: 11 },
      itemWidth: 12,
      itemHeight: 8,
      itemGap: 18,
    },
    xAxis: {
      type: "category",
      data: data?.map((item) => item.interval),
      axisLine: { lineStyle: { color: gridLineColor } },
      axisTick: { show: false },
      axisLabel: {
        color: labelColor,
        fontSize: 10,
        formatter: (v) => axisTimeLabel(v, multiDay),
      },
      splitLine: { show: false },
    },
    yAxis: [
      {
        type: "value",
        name: "MW",
        nameTextStyle: { color: labelColor, fontSize: 10, padding: [0, 0, 0, -40] },
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: labelColor,
          fontSize: 10,
          formatter: (v) => v.toFixed(2),
        },
        splitLine: { lineStyle: { color: gridLineColor, type: "dashed" } },
      },
      {
        type: "value",
        name: "kW",
        position: "right",
        nameTextStyle: { color: labelColor, fontSize: 10 },
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: labelColor,
          fontSize: 10,
          formatter: (v) => v.toFixed(0),
        },
        splitLine: { show: false },
      },
    ],
    series,
  };
}

export default function EChartsOperatorGraph({
  pollingInterval = 0,
  request,
  // The dashboard's chart is the last 24 hours; Historical names its chosen range (NEU-1130).
  title = "Demand · last 24 hours",
}) {
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const chartRef = useRef(null);

  const {
    data: lspsState,
    isLoading: l1,
    isError: e1,
  } = useGetLSPSStateQuery(null, { pollingInterval });
  const {
    data: microServiceGraphData,
    isLoading: l2,
    isError: e2,
  } = useGetMicroServiceMonitoringGraphDataQuery(request, { pollingInterval });
  const {
    data: microserviceForcastState,
    isLoading: l3,
    isError: e3,
  } = useGetMicroServiceMonitoringForcastQuery(null, { pollingInterval });
  const {
    data: targetDataNow,
    isLoading: l4,
    isError: e4,
  } = useGetTargetDataNowQuery(null, { pollingInterval });

  const handleDownloadCsv = () => {
    const rows = microServiceGraphData || [];
    if (rows.length === 0) return;
    let csv =
      "Timestamp (SAST),Current Demand (MW),Integrated Demand (MW),Target (MW),Tjommie Load (kW)\r\n";
    const target = targetDataNow?.target
      ? (targetDataNow.target / 1000000).toFixed(4)
      : "";
    rows.forEach((item) => {
      const dt = formatSAST(item.interval);
      const demand = item.current_demand != null ? (item.current_demand / 1000000).toFixed(4) : "";
      const integrated = item.latest_integrated_demand != null ? (item.latest_integrated_demand / 1000000).toFixed(4) : "";
      const tjommie = item.geyser_demand != null ? (item.geyser_demand / 1000).toFixed(4) : "";
      csv += `${dt},${demand},${integrated},${target},${tjommie}\r\n`;
    });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Historical_Demand_${dayjs().format("YYYY-MM-DD")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadPng = () => {
    const url = chartRef.current?.getEchartsInstance()?.getDataURL({ type: "png", pixelRatio: 2, backgroundColor: "#161B22" });
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = `Historical_Demand_${dayjs().format("YYYY-MM-DD")}.png`;
    a.click();
  };

  if (l1 || l2 || l3 || l4) {
    return (
      <Grid
        item
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 2,
        }}
      >
        <CircularProgress size={20} sx={{ color: "#2E93FA" }} />
        <Typography sx={{ color: labelColor }}>Loading graph data…</Typography>
      </Grid>
    );
  }

  if (e1 || e2 || e3 || e4) {
    return (
      <Grid item sx={{ width: "100%", p: 2 }}>
        <Typography sx={{ color: "#e05050" }}>
          Error loading graph data
        </Typography>
      </Grid>
    );
  }

  const data = microServiceGraphData || [];
const forecastPoints = (microserviceForcastState || [])
  .slice(0, 2)
  .filter((f) => f.forecast && f.forecast_timestamp)
  .map((f) => {
    const idx = data.findIndex(
      (d) =>
        new Date(d.interval).getTime() ===
        new Date(f.forecast_timestamp).getTime()
    );

    return idx >= 0
      ? [idx, f.forecast / 1000000]
      : null;
  })
  .filter(Boolean);
  const series = [
    lineSeries(
      "Current Demand",
      "#2E93FA",
      toCategory(data, (i) => (i.current_demand ? i.current_demand / 1000000 : null)),
      false,
      true,
      0,
    ),
    lineSeries(
      "Target",
      "#26BF00",
      toCategory(data, () =>
        targetDataNow?.target ? targetDataNow.target / 1000000 : null,
      ),
      true,
      false,
      0,
    ),
    lineSeries(
      "Max Return",
      "#FF5C00",
      // The default profile's Max Return in MW. The chart read max_return, which the
      // endpoint never sent, so the line never drew (NEU-1130).
      toCategory(data, () => lspsState?.max_return_mw ?? null),
      true,
      false,
      0,
    ),
    barSeries(
      "Integrated Demand",
      "#A53838",
      toCategory(data, (i) =>
        i.latest_integrated_demand
          ? i.latest_integrated_demand / 1000000
          : null,
      ),
      0,
    ),
    lineSeries(
      "Tjommie Load",
      "#FDB940",
      toCategory(data, (i) =>
        i.geyser_demand != null ? i.geyser_demand / 1000 : null,
      ),
      false,
      true,
      1,
    ),
    {
      name: "Forecast",
      type: "scatter",
      symbolSize: 10,
      yAxisIndex: 0,
      itemStyle: { color: "#DC6CFE" },
      data: forecastPoints,
    },
  ];

  return (
    <Grid item sx={{ width: "100%" }}>
      <Box
        sx={{
          background: "#161B22",
          border: "1px solid rgba(255,255,255,0.06)",
          borderRadius: "12px",
          overflow: "hidden",
          padding: "20px 20px 16px",
        }}
      >
        {/* Card header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            mb: "16px",
          }}
        >
          <Box>
            <Typography
              sx={{ fontSize: "0.95rem", fontWeight: 600, color: "#fff" }}
            >
              {title}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.78rem",
                color: "rgba(255,255,255,0.45)",
                mt: "2px",
              }}
            >
              Sum of Metro incomers (MW, left axis) · Tjommie load (kW, right axis)
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
            <Box
              sx={{
                fontSize: "0.72rem",
                color: "rgba(255,255,255,0.45)",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: "999px",
                padding: "4px 10px",
                whiteSpace: "nowrap",
              }}
            >
              User TZ · {userTimezone}
            </Box>
            <GraphDownloadButton onDownloadCsv={handleDownloadCsv} onDownloadPng={handleDownloadPng} />
          </Box>
        </Box>

        <ReactECharts
          ref={chartRef}
          option={buildOption(series, data)}
          style={{ height: 320 }}
          notMerge
        />
      </Box>
    </Grid>
  );
}
